import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Type-ahead single-select combobox with an "All" option pinned at the top.
 * Renders a native input inside the popover for filtering options client-side.
 *
 * Props
 *  - options: [{ value, label }]
 *  - value: current selected value (string) or "all"
 *  - onChange: (nextValue) => void
 *  - placeholder: shown on the trigger button when value === "all"
 *  - allLabel: text for the "all" option (default "All")
 *  - allValue: the "select-all" value token (default "all")
 *  - testId
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select…",
  allLabel = "All",
  allValue = "all",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [q, options]);

  const selectedLabel = useMemo(() => {
    if (value === allValue) return placeholder;
    const hit = options.find((o) => o.value === value);
    return hit ? hit.label : placeholder;
  }, [value, options, placeholder, allValue]);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
    setQ("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid={testId}
          className="h-10 w-full justify-between font-normal text-left"
        >
          <span className={value === allValue ? "text-gray-500" : "text-gray-900 truncate"}>
            {selectedLabel}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              autoFocus
              placeholder="Type to search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-9"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto py-1" data-testid={testId ? `${testId}-options` : undefined}>
          <button
            type="button"
            onClick={() => pick(allValue)}
            className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${
              value === allValue ? "font-semibold text-[#9B1C1C]" : "text-gray-700"
            }`}
            data-testid={testId ? `${testId}-option-all` : undefined}
          >
            {value === allValue ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
            {allLabel}
          </button>
          {filtered.map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => pick(o.value)}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${
                value === o.value ? "font-semibold text-[#9B1C1C]" : "text-gray-700"
              }`}
              data-testid={testId ? `${testId}-option-${o.value}` : undefined}
            >
              {value === o.value ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
              <span className="truncate">{o.label}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-xs text-gray-500 text-center">No matches</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
